<template>
  <div class="view-header">
    <!-- Left Section: Lottery Information -->
    <div class="lottery-info">
      <div class="flag-container">
        <img 
          v-if="lottery?.game_logo" 
          :src="getGameLogoUrl(lottery.game_logo)" 
          :alt="lottery.country + ' flag'" 
          class="flag" 
        />
      </div>
      <div class="lottery-details">
        <h1 class="lottery-name">{{ lottery?.game_name || 'Loading...' }}</h1>
      </div>
    </div>

    <!-- Middle Section: Countdown Timer -->
    <div class="countdown-section">
      <div class="countdown">
        <div class="time-block">
          <div class="time">{{ String(countdown.hours).padStart(2, '0') }}</div>
          <div class="label">{{ $t('time.hours') }}</div>
        </div>
        <div class="time-block">
          <div class="time">{{ String(countdown.minutes).padStart(2, '0') }}</div>
          <div class="label">{{ $t('time.mins') }}</div>
        </div>
        <div class="time-block">
          <div class="time">{{ String(countdown.seconds).padStart(2, '0') }}</div>
          <div class="label">{{ $t('time.secs') }}</div>
        </div>
      </div>
    </div>

    <!-- Right Section: Back Button -->
    <div class="action-section">
      <button class="back-button" @click="$emit('go-back')">
        BACK TO LOTTERIES
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits } from 'vue';
import { getGameLogoUrl } from '../../utils/resultParser';

interface Lottery {
  game_name?: string;
  country?: string;
  game_logo?: string;
  draw_date?: string;
  cutoff?: string;
}

interface Props {
  lottery: Lottery | null;
  countdown: {
    hours: number;
    minutes: number;
    seconds: number;
  };
}

defineProps<Props>();

defineEmits<{
  'go-back': [];
}>();

</script>

<style scoped>
.view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--bg-view-header, var(--lottery-card-gradient));
  padding: 0px 16px;
  margin-bottom: 1px;
  border-bottom: 1px solid var(--border-color);
  min-height: 60px;
}

.lottery-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.flag-container {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid var(--text-white);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-card);
}

.flag {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.lottery-details {
  display: flex;
  flex-direction: column;
}

.lottery-name {
  color: var(--text-white);
  font-size: 18px;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
}

.countdown-section {
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-dark-card);
  padding: 12px 20px;
  margin-right: 8px;
}

.countdown {
  display: flex;
  gap: 16px;
  align-items: center;
}

.time-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.time-block:nth-child(1),
.time-block:nth-child(2),
.time-block:nth-child(3) {
  width: 32px !important;
  height: 32px;
  opacity: 1;
  color: var(--text-white);
  font-size: 24px;
}

.time-block:nth-child(1) .time,
.time-block:nth-child(2) .time,
.time-block:nth-child(3) .time {
  color: var(--text-white) !important;
  font-size: 24px;
  font-weight: bold;
}

.time-block:nth-child(1) .label,
.time-block:nth-child(2) .label,
.time-block:nth-child(3) .label {
  color: var(--text-white) !important;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.time {
  color: var(--text-accent) !important;
  font-size: 24px;
  font-weight: bold;
  line-height: 1;
}

.label {
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.action-section {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.back-button {
  background: transparent;
  color: var(--text-white) !important;
  border: 2px solid var(--accent-color);
  padding: 8px 24px;
  border-radius: 13.1px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  text-transform: uppercase;
  font-size: 14px;
}

.back-button:hover {
  background: var(--accent-color);
  color: var(--text-white);
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .view-header {
    display: grid;
    grid-template-columns: auto 1fr auto;
    gap: 12px;
    align-items: center;
    padding: 16px;
    min-height: auto;
    background: var(--lottery-card-gradient);
    border-radius: 0;
    margin: 0;
    width: 100%;
  }
  
  .lottery-info {
    justify-content: flex-start;
    gap: 12px;
    align-items: center;
  }
  
  .flag-container {
    width: 32px;
    height: 32px;
    border: 1px solid var(--text-white);
    flex-shrink: 0;
  }
  
  .lottery-name {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin: 0;
  }
  
  .countdown-section {
    justify-content: center;
    background: var(--bg-dark-card);
    padding: 8px 12px;
    height: 100%;
    display: flex;
    align-items: center;
  }
  
  .countdown {
    gap: 8px;
    justify-content: flex-end;
  }
  
  .time-block {
    text-align: center;
  }
  
  .time {
    font-size: 16px;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 1px;
    color: var(--text-white);
  }
  
  .label {
    font-size: 8px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    opacity: 0.9;
  }
  
  .action-section {
    display: none;
  }
}

@media (max-width: 480px) {
  .countdown {
    gap: 8px;
  }
  
  .time {
    font-size: 18px;
  }
  
  .label {
    font-size: 9px;
  }
}
</style>

<script lang="ts">
export default {
  name: "DesktopHeader",
};
</script>
